import React, { useState, useEffect, useCallback, useRef, useContext } from 'react';
// Remove useNavigate since it's not being used
// import { useNavigate } from 'react-router-dom';
import apiConfig from '../config/apiConfig';
import { useAppState } from '../context/AppStateContext';
import { UserContext } from '../context/UserContext';
import PageTitle from '../components/PageTitle';
import CooldownTimer from '../components/CooldownTimer';
import './Training.css';

const Training = () => {
  const [skills, setSkills] = useState([]);
  const [loading, setLoading] = useState(false);
  const [trainingInProgress, setTrainingInProgress] = useState(false);
  const [error, setError] = useState(null);
  const [trainingResult, setTrainingResult] = useState(null);
  const [selectedSkill, setSelectedSkill] = useState(null);
  const [skillsLoaded, setSkillsLoaded] = useState(false);
  const [autoRefreshTimer, setAutoRefreshTimer] = useState(null);
  
  // Add a ref to track initial fetch
  const initialFetchDone = useRef(false);
  
  const { 
    inJail, 
    jailReleaseTime, 
    trainingCooldown,
    isTrainingAvailable,
    updateCooldownsFromAPI,
    updateTrainingCooldown
  } = useAppState();
  
  // Properly access the UserContext
  const userContext = useContext(UserContext);
  const { user, refreshCharacterStats } = userContext;
  
  // Remove the unused navigate variable
  // const navigate = useNavigate();

  // Add refs for throttling API requests
  const isFetchingSkillsRef = useRef(false);
  const lastSkillsFetchRef = useRef(0);
  const lastCharacterRefreshRef = useRef(0);
  
  // Function to fetch skills with completion percentages - fix dependency array
  const fetchSkills = useCallback(async () => {
    // Add extra throttling to prevent excessive API calls
    const now = Date.now();
    
    // Skip if already fetching
    if (isFetchingSkillsRef.current) {
      console.log('Skills fetch already in progress, skipping duplicate call');
      return;
    }
    
    // Apply a 10-second throttle, but only for non-initial fetches
    if (initialFetchDone.current && now - lastSkillsFetchRef.current < 10000) {
      console.log('Skills were fetched recently, throttling call');
      return;
    }
    
    console.log('fetchSkills called. Loading:', loading, 'inJail:', inJail, 'skillsLoaded:', skillsLoaded);
    
    // Don't refetch if already loading or skills already loaded (except for initial fetch)
    if (loading) {
      console.log('Skills fetch already in progress');
      return;
    }
    
    if (skillsLoaded && initialFetchDone.current) {
      console.log('Skills already loaded, skipping fetch');
      return;
    }
    
    console.log('Fetching skills...');
    
    // Check jail status before fetching
    if (inJail) {
      console.log('User is in jail, skipping skills fetch');
      setError('You cannot train while in jail. Wait until your sentence is complete.');
      return;
    }
    
    try {
      setLoading(true);
      isFetchingSkillsRef.current = true;
      lastSkillsFetchRef.current = now;
      
      // Only refresh character data if it's been a while
      if (now - lastCharacterRefreshRef.current > 60000) { // 1 minute
        await refreshCharacterStats(true);
        lastCharacterRefreshRef.current = now;
      }
      
      // Then fetch skill-specific data
      const response = await fetch(apiConfig.training, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error(`Failed to fetch skills: ${response.status}`);
      }
      
      const data = await response.json();
      console.log('Skills fetched successfully:', data);
      
      // Ensure completion is a number for proper calculations
      const processedSkills = Array.isArray(data) ? data.map(skill => ({
        ...skill,
        completion: typeof skill.completion === 'number' ? skill.completion : parseFloat(skill.completion) || 0
      })) : [];
      
      setSkills(processedSkills);
      setSkillsLoaded(true);
      setError(null);
    } catch (err) {
      console.error('Error fetching skills:', err);
      setError('Failed to load skills. Please try again.');
    } finally {
      setLoading(false);
      isFetchingSkillsRef.current = false;
    }
  }, [inJail, loading, skillsLoaded, refreshCharacterStats]); // Add skillsLoaded to dependencies
  
  // Simplified useEffect with proper dependency array and throttling
  useEffect(() => {
    // Always fetch skills once when the component mounts
    if (!initialFetchDone.current && !loading && !inJail) {
      console.log('Training component: Initial skills fetch');
      initialFetchDone.current = true;
      // Immediate fetch on first visit
      fetchSkills();
    }
  }, [fetchSkills, loading, inJail]);
  
  // Fetch cooldown information only once when the component mounts
  useEffect(() => {
    // Only refresh if it hasn't been done recently
    const now = Date.now();
    if (now - lastCharacterRefreshRef.current > 30000 && user?.playerCharacter?.id) {
      lastCharacterRefreshRef.current = now;
      refreshCharacterStats(true);
    }
  }, [refreshCharacterStats, user]);
  
  // Redirect if in jail
  useEffect(() => {
    if (inJail) {
      setError('You cannot train while in jail. Wait until your sentence is complete.');
    }
  }, [inJail]);
    
  // Check for lastTraining in character data - optimized with throttle
  useEffect(() => {
    if (trainingCooldown || !user?.playerCharacter?.lastTraining) {
      return;
    }
    
    // Throttle this check to prevent excessive updates
    const lastUpdateTime = window.__LAST_CHARACTER_COOLDOWN_CHECK || 0;
    if (Date.now() - lastUpdateTime < 30000) { // Increased to 30 seconds
      return;
    }
    
    window.__LAST_CHARACTER_COOLDOWN_CHECK = Date.now();
    console.log('Found lastTraining time in character data:', user.playerCharacter.lastTraining);
    
    // Only update if it's in the future
    const lastTrainingDate = new Date(user.playerCharacter.lastTraining);
    if (lastTrainingDate > new Date()) {
      updateCooldownsFromAPI({
        trainingCooldown: user.playerCharacter.lastTraining
      });
    }
  }, [user, trainingCooldown, updateCooldownsFromAPI]);
  
  // Add useEffect to handle cooldown expiration
  useEffect(() => {
    if (trainingCooldown) {
      const cooldownDate = new Date(trainingCooldown);
      const now = new Date();
      
      if (cooldownDate > now) {
        // Calculate time until cooldown expires
        const timeUntilExpiration = cooldownDate.getTime() - now.getTime();
        
        // Set a timer to refresh skills when cooldown expires
        const cooldownTimer = setTimeout(() => {
          console.log('Cooldown expired, refreshing skills');
          setSkillsLoaded(false);
          fetchSkills();
        }, timeUntilExpiration + 100); // Add 100ms buffer
        
        return () => clearTimeout(cooldownTimer);
      }
    }
  }, [trainingCooldown, fetchSkills]);
  
  // Function to train a skill
  const trainSkill = async (skillId) => {
    // Don't allow training if inJail
    if (inJail) {
      setError('You cannot train while in jail. Wait until your sentence is complete.');
      return;
    }
    
    // Don't allow training if on cooldown
    if (trainingCooldown && !isTrainingAvailable()) {
      setError('You are still recovering from your last training session.');
      return;
    }
    
    const skill = skills.find(s => s.id === skillId);
    setSelectedSkill(skill);
    setTrainingInProgress(true);
    setError(null);
    
    try {
      const response = await fetch(apiConfig.doTraining(skillId), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include'
      });
      
      const data = await response.json();
      
      if (response.ok) {
        console.log('Training result:', data);
        
        // SIMPLIFIED: Handle cooldown updates through standard React state
        if (data.cooldown) {
          try {    
            const cooldownDate = new Date(data.cooldown);
            
            // Update React state through the normal AppState context
            updateTrainingCooldown(cooldownDate);
            
            // Store in localStorage for persistence (but no direct DOM updates)
            localStorage.setItem('training_cooldown', cooldownDate.toISOString());
          } catch (err) {
            console.error('Error processing training cooldown:', err);
          }
        }
        
        // Handle the returned skill data
        const updatedSkill = data.skill || data; // Sometimes the skill is directly in the response
        
        // Make sure completion is a number
        if (updatedSkill && updatedSkill.completion !== undefined) {
          updatedSkill.completion = typeof updatedSkill.completion === 'number' 
            ? updatedSkill.completion 
            : parseFloat(updatedSkill.completion) || 0;
        }
        
        // Show training result with updated skill information
        setTrainingResult({
          success: true,
          message: data.message || 'You have successfully trained your skill.',
          cooldown: data.cooldown,
          skill: updatedSkill // Use the updated skill data from the response
        });
        
        // Update the skill in the skills array without refreshing all skills
        if (updatedSkill && updatedSkill.id) {
          setSkills(prevSkills => 
            prevSkills.map(s => 
              s.id === updatedSkill.id ? updatedSkill : s
            )
          );
          
          // Also refresh character data to get updated cooldowns
          refreshCharacterStats(true);
        } else {
          // If no skill data returned, refresh all skills
          setSkillsLoaded(false);
          setTimeout(() => {
            fetchSkills();
          }, 300);
        }
        
        // Replace aggressive timer with a gentler approach
        if (autoRefreshTimer) {
          clearTimeout(autoRefreshTimer);
        }
        
        // Use a longer delay to reduce API pressure
        const timer = setTimeout(() => {
          console.log('Delayed UI update after training');
          
          // Don't refresh character data immediately, component state is already updated
          setSkillsLoaded(false);
          fetchSkills();
        }, 5000); // Longer delay (5 seconds)
        
        setAutoRefreshTimer(timer);
      } else {
        console.error('Training failed:', data);
        setTrainingResult({
          success: false,
          message: data.message || 'Training failed. Please try again.',
          cooldown: data.cooldown,
          inJail: data.inJail || false
        });
        
        // Update cooldowns or jail status if provided
        if (data.cooldown || data.inJail) {
          updateCooldownsFromAPI({
            trainingCooldown: data.cooldown,
            inJail: data.inJail || false,
            jailReleaseTime: data.jailTime || null
          });
          
          // Also refresh character data
          refreshCharacterStats(true);
        }
      }
    } catch (err) {
      console.error('Error training skill:', err);
      setTrainingResult({
        success: false,
        message: 'An error occurred while training. Please try again later.'
      });
    } finally {
      setTrainingInProgress(false);
    }
  };
  
  // Close training result modal - simplified
  const closeTrainingResult = () => {
    // Clear auto-refresh timer if user closes the modal
    if (autoRefreshTimer) {
      clearTimeout(autoRefreshTimer);
      setAutoRefreshTimer(null);
    }
    
    // SIMPLIFIED: Handle cooldown through normal React state
    if (trainingResult && trainingResult.cooldown) {
      try {
        const cooldownDate = new Date(trainingResult.cooldown);
        
        // Update the cooldown state once through the normal React flow
        updateTrainingCooldown(cooldownDate);
      } catch (err) {
        console.error('Error in cooldown update:', err);
      }
    }
    
    setTrainingResult(null);
    setSelectedSkill(null);
  };

  // Better cleanup on component unmount
  useEffect(() => {
    return () => {
      // Clear timers
      if (autoRefreshTimer) {
        clearTimeout(autoRefreshTimer);
      }
      
      // Clean up any cooldown-specific body classes
      document.body.classList.remove('cooldown-active');
    };
  }, [autoRefreshTimer]);
  
  // Format the completion percentage
  const formatCompletion = (completion) => {
    // Ensure completion is a number and display as a whole number percentage
    const value = typeof completion === 'number' ? completion : parseFloat(completion) || 0;
    return `${value}%`;
  };
  
  // Display debug info for cooldown
  useEffect(() => {
    console.log('Current training cooldown:', trainingCooldown ? new Date(trainingCooldown).toLocaleString() : 'None');
  }, [trainingCooldown]);
  
  return (
    <div className="training-page">
      <PageTitle title="Training" />
      
      {inJail && (
        <div className="jail-warning">
          <span className="jail-icon">🔒</span>
          <div>
            <p>You cannot train while imprisoned.</p>
            {jailReleaseTime && (
              <p className="release-time">
                Release time: {new Date(jailReleaseTime).toLocaleString()}
              </p>
            )}
          </div>
        </div>
      )}
      
      {trainingCooldown && !isTrainingAvailable() && (
        <div className="cooldown-warning">
          <span className="cooldown-icon">⏳</span>
          <div>
            <p>You are still recovering from your last training session.</p>
            <p className="cooldown-timer-display">
              <CooldownTimer 
                targetTime={trainingCooldown} 
                onComplete={() => {
                  // Force re-render when cooldown completes
                  setSkillsLoaded(false);
                  fetchSkills();
                }}
              />
            </p>
          </div>
        </div>
      )}
      
      {error && (
        <div className="error-message">
          <p>{error}</p>
        </div>
      )}
      
      {/* Training result modal */}
      {trainingResult && (
        <div className="training-result-overlay">
          <div className="training-result-modal">
            <h3>{trainingResult.success ? 'Training Successful!' : 'Training Failed'}</h3>
            <p>{trainingResult.message}</p>
            
            {trainingResult.skill && trainingResult.success && (
              <div className="trained-skill">
                <h4>{trainingResult.skill.name}</h4>
                <p>Current level: {trainingResult.skill.level}</p>
                <div className="progress-bar">
                  <div 
                    className="progress-fill" 
                    style={{ width: formatCompletion(trainingResult.skill.completion) }}
                  ></div>
                </div>
                <p className="completion-text">Progress: {formatCompletion(trainingResult.skill.completion)}</p>
              </div>
            )}
            
            {trainingResult.cooldown && (
              <div className="cooldown-info">
                <p>Training cooldown:</p>
                <CooldownTimer 
                  targetTime={trainingResult.cooldown}
                  onComplete={() => {
                    console.log('Training cooldown completed');
                    // Refresh character data when cooldown completes
                    refreshCharacterStats(true);
                  }}
                />
              </div>
            )}
            
            <button className="close-button" onClick={closeTrainingResult}>Close</button>
          </div>
        </div>
      )}
      
      {loading ? (
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading available skills...</p>
        </div>
      ) : (
        <div className="skills-container">
          <h2>Available Skills</h2>
          
          {/* Display global cooldown at the top if active */}
          {trainingCooldown && !isTrainingAvailable() && (
            <div className="global-cooldown-banner">
              <span>Training cooldown active:</span>
              <CooldownTimer 
                targetTime={trainingCooldown}
                onComplete={() => {
                  // Refresh when cooldown completes
                  setSkillsLoaded(false);
                  fetchSkills();
                }}
              />
            </div>
          )}
          
          {skills.length === 0 ? (
            <p className="no-skills">No skills available for training at this time.</p>
          ) : (
            <div className="skills-grid">
              {skills.map(skill => (
                <div key={skill.id} className="skill-card">
                  <h3 className="skill-name">{skill.name}</h3>
                  <p className="skill-description">{skill.description}</p>
                  <div className="skill-level">
                    <span>Level: {skill.level}</span>
                    <div className="progress-bar">
                      <div 
                        className="progress-fill" 
                        style={{ width: formatCompletion(skill.completion) }}
                      ></div>
                    </div>
                    <span className="completion-text">Progress: {formatCompletion(skill.completion)}</span>
                  </div>
                  <button 
                    className="train-button" 
                    onClick={() => trainSkill(skill.id)}
                    disabled={inJail || trainingInProgress || (trainingCooldown && !isTrainingAvailable())}
                  >
                    {trainingInProgress && selectedSkill?.id === skill.id 
                      ? 'Training...' 
                      : trainingCooldown && !isTrainingAvailable()
                        ? 'On Cooldown'
                        : 'Train Skill'}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      
      {/* Training in progress overlay */}
      {trainingInProgress && (
        <div className="training-overlay">
          <div className="training-progress">
            <div className="training-spinner"></div>
            <p>Training {selectedSkill?.name}...</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default Training;
